import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  TextField,
  styled,
} from '@mui/material'
import React, { useEffect, useState, useMemo } from 'react'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import { ACTIVITY_TYPES, FIELD_TYPES, TEAM_SUB_TYPES, TEAM_TYPES } from '../../../utility/constants'
import { fieldTemplateService } from '../../../services/fieldTemplateService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { adminProcessService } from '../../../services/admin/process'
import { adminTeamsService } from '../../../services/admin/teams'
import { listDataService } from '../../../services/listDataService'
import DefaultValueField from './DefaultValueField'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import ConditionalEnableDisableUI from './ConditionalEnableDisableUI'
import FieldConstraintUI from './FieldConstraintUI'
import ParentFieldSetupUI from './ParentFieldSetupUI'
import FieldVisibilityProtectionUI from './FieldVisibilityProtectionUI'
import TableFieldConstraintUI from './TableFieldConstraintUI'
import FieldComputationSetupUI from './FieldComputationSetupUI'
import { userConfig } from '../../../services/userConfigServices'
import { toast } from 'react-toastify'
import ImportFieldSelectionUI from './ImportFieldSelectionUI'
import PivotFieldFilterSetup from './PivotFieldFilterSetup'
import PreventProcessCreationUI from './PreventProcessCreationUI'
import RecurringTableSetup from './RecurringTableSetup'
import AggregationTables from './AggregationTables'
import ValidationRulesUI from './ValidationRulesUI'

const booleanProps = [
  'is_subfield',
  'is_required',
  'is_assignment_field',
  'is_read_only',
  'is_computed',
  'is_hidden_in_process_dashboard',
  'is_derived_field',
  'is_selectable_table',
  'allow_add_rows',
  'allow_delete_rows',
  'allow_excel_upload',
  'is_imported_field',
  'is_hidden',
  'is_pivot_field',
  'is_one_time_input',
  'is_process_counter',
  'is_lrp_display',
  'is_aggregation_table',
  'is_visible_in_my_tasks_card',
]

const allColumns = [
  'id',
  'field_template_name',
  'data_structure',
  'sequence',
  'description',
  'created_at',
  'modified_at',
  'created_by',
  'modified_by',
  'list_id',
  'page_template_id',
  'is_subfield',
  'is_required',
  'metadata',
  'is_assignment_field',
  'is_read_only',
  'is_computed',
  'is_hidden_in_process_dashboard',
  'is_derived_field',
  'derived_from_field_template_id',
  'is_selectable_table',
  'allow_add_rows',
  'allow_delete_rows',
  'allow_excel_upload',
  'pivot_field_template_id',
  'is_imported_field',
  'imported_from_field_template_id',
  'is_hidden',
  'is_pivot_field',
  'import_from_process_template_id',
  'subfield_column_width_in_rem',
  'field_width_in_rem_for_process_dashboard',
  'is_one_time_input',
  'dynamic_field_type',
  'is_process_counter',
  'num_decimal_places',
  'is_lrp_display',
  'default_value',
  'is_aggregation_table',
  'aggregation_parent_table_field_template_id',
  'is_visible_in_my_tasks_card',
  'process_counter_initial_value',
  'validation_rules',
]

const transformFieldPropForAPI = (obj) =>
  Object.entries(obj).reduce(
    (acc, [key, value]) => ({ ...acc, [key]: booleanProps.includes(key) ? value == 'yes' : value }),
    {},
  )

const transformFieldPropForUI = (obj) =>
  Object.entries(obj || {}).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: booleanProps.includes(key) ? (value ? 'yes' : 'no') : value,
    }),
    {},
  )

const onlyFieldColumns = (obj, keys) =>
  Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key)))

const TEXT_KEYS = ['minLength', 'maxLength', 'regExPattern']
const NUMERIC_KEYS = ['min', 'max']
const SHARED_KEYS = ['errorMessage']

const getSchemaType = (dataStructure) => {
  if ([FIELD_TYPES.TEXT, FIELD_TYPES.BIGTEXT].includes(dataStructure)) return 'text'
  if ([FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY].includes(dataStructure)) return 'numeric'
  return null
}

const pruneValidationRules = (rules, nextDataStructure) => {
  if (!rules) return null
  const schemaType = getSchemaType(nextDataStructure)

  const typeSpecificKeys =
    schemaType === 'text' ? TEXT_KEYS : schemaType === 'numeric' ? NUMERIC_KEYS : []

  const hasTypeSpecificKeys = typeSpecificKeys.some(
    (k) => rules[k] !== null && rules[k] !== undefined && rules[k] !== '',
  )

  if (!hasTypeSpecificKeys) return null

  const allowedKeys = [...typeSpecificKeys, ...SHARED_KEYS]

  const pruned = Object.fromEntries(
    Object.entries(rules).filter(
      ([k, v]) => allowedKeys.includes(k) && v !== null && v !== undefined && v !== '',
    ),
  )

  return Object.keys(pruned).length > 0 ? pruned : null
}

const GroupHeader = styled('Box')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  fontWeight: 'bold',
  backgroundColor: theme.palette.mode === 'light' ? '#f7adff' : '#f7adff',
}))

const GroupItems = styled('ul')({ padding: 0 })

function UserFilterSettings({ fieldTemplate, setFieldTemplate }) {
  const { user } = useAuthContext()
  const [listConfigs, setListConfigs] = useState({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!user) return
    setLoading(true)
    adminTeamsService
      .getAllCompanyListConfigs(user)
      .then((data) => setListConfigs(data || {}))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [user])

  const filter = fieldTemplate?.dynamic_field_type?.filter || {}

  const updateFilter = (patch) =>
    setFieldTemplate({
      ...fieldTemplate,
      dynamic_field_type: {
        ...fieldTemplate.dynamic_field_type,
        filter: { ...filter, ...patch },
      },
    })

  const categoryItems = useMemo(
    () => listConfigs?.category?.items || [],
    [listConfigs?.category?.items],
  )
  const userTypeItems = useMemo(
    () => listConfigs?.user_type?.items || [],
    [listConfigs?.user_type?.items],
  )
  const workStatusItems = useMemo(
    () => listConfigs?.work_status?.items || [],
    [listConfigs?.work_status?.items],
  )

  const [availableUserTypes, setAvailableUserTypes] = useState([])

  useEffect(() => {
    if (!filter.category || filter.category.length === 0) {
      setAvailableUserTypes(userTypeItems)
      return
    }
    if (!user) return
    Promise.all(filter.category.map((catId) => listDataService.getChildrenByParent(user, catId)))
      .then((results) => {
        const childIds = new Set()
        results.forEach((children) => {
          if (Array.isArray(children)) children.forEach((child) => childIds.add(child.id))
        })
        const filtered = userTypeItems.filter((ut) => childIds.has(ut.id))
        setAvailableUserTypes(filtered.length > 0 ? filtered : userTypeItems)
      })
      .catch(() => setAvailableUserTypes(userTypeItems))
  }, [JSON.stringify(filter.category), userTypeItems, user])

  const isAccountActive = filter.is_account_active !== false

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.2,
        backgroundColor: '#fafafa',
      }}
    >
      <Box
        sx={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', mb: 0.5 }}
      >
        User List Filter Options
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ fontSize: 13, color: '#444', flex: 1 }}>
          Show only active accounts?{' '}
          <Box component="span" sx={{ fontSize: 11, color: '#999' }}>
            (default: yes)
          </Box>
        </Box>
        <Select
          size="small"
          value={isAccountActive ? 'yes' : 'no'}
          onChange={(e) => updateFilter({ is_account_active: e.target.value === 'yes' })}
          sx={{ minWidth: 80 }}
        >
          <MenuItem value="yes">Yes</MenuItem>
          <MenuItem value="no">No</MenuItem>
        </Select>
      </Box>

      <Autocomplete
        multiple
        loading={loading}
        size="small"
        options={categoryItems}
        getOptionLabel={(opt) => opt.value || ''}
        value={categoryItems.filter((it) => (filter.category || []).includes(it.id))}
        onChange={(_, selected) =>
          updateFilter({
            category: selected.length > 0 ? selected.map((s) => s.id) : null,
            user_type: null,
          })
        }
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderInput={(params) => (
          <TextField {...params} label="Filter by Category (blank = all)" size="small" />
        )}
      />

      <Autocomplete
        multiple
        loading={loading}
        size="small"
        options={availableUserTypes}
        getOptionLabel={(opt) => opt.value || ''}
        value={availableUserTypes.filter((it) => (filter.user_type || []).includes(it.id))}
        onChange={(_, selected) =>
          updateFilter({ user_type: selected.length > 0 ? selected.map((s) => s.id) : null })
        }
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderInput={(params) => (
          <TextField {...params} label="Filter by User Type (blank = all)" size="small" />
        )}
      />

      <Autocomplete
        multiple
        loading={loading}
        size="small"
        options={workStatusItems}
        getOptionLabel={(opt) => opt.value || ''}
        value={workStatusItems.filter((it) => (filter.work_status || []).includes(it.id))}
        onChange={(_, selected) =>
          updateFilter({ work_status: selected.length > 0 ? selected.map((s) => s.id) : null })
        }
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderInput={(params) => (
          <TextField {...params} label="Filter by Work Status (blank = all)" size="small" />
        )}
      />
    </Box>
  )
}

const EditFieldTemplateDialog = ({
  initialFieldTemplate = null,
  importedFieldList = [],
  lists = [],
  ftData = [],
  processTemplateId,
  activityTemplateId,
  activityTemplate,
  setDialogOpen = () => {},
  onChangeSave = async () => {},
  onFieldDelete = () => {},
}) => {
  const { user } = useAuthContext()
  const [fieldTemplate, setFieldTemplate] = useState(transformFieldPropForUI(initialFieldTemplate))
  const [derivableFieldTemplates, setDerivableFieldTemplate] = useState([])
  const [parentFieldTemplates, setParentFieldTemplates] = useState([])
  const [processes, setProcesses] = useState([])
  const [pivotFields, setPivotFields] = useState([])
  const [importableFields, setImportableFields] = useState([])
  const [selectedListItems, setSelectedListItems] = useState([])
  const [selectedTabValue, setSelectedTabValue] = useState('1')
  const [assignmentFieldData, setAssignmentFieldData] = useState({})
  const [tableField, setTableField] = useState(null)
  const [pages, setPages] = useState([])
  const [teamOptions, setTeamOptions] = useState({ types: [], subtypes: [], mapping: {} })

  useEffect(() => {
    if (fieldTemplate?.dynamic_field_type?.type === 'teams' && user) {
      adminTeamsService.getTeamTypeAndSubTypes(user).then((data) => {
        setTeamOptions({
          types: data.teamTypes || [],
          subtypes: data.teamSubTypes || [],
          mapping: data.typeToSubtypes || {},
        })
      })
    }
  }, [fieldTemplate?.dynamic_field_type?.type, user])

  const filteredSubtypes = useMemo(() => {
    const selectedTypes = fieldTemplate?.dynamic_field_type?.subtypes || []
    if (selectedTypes.length === 0) return teamOptions.subtypes
    const filtered = new Set()
    selectedTypes.forEach((type) => {
      ;(teamOptions.mapping[type] || []).forEach((st) => filtered.add(st))
    })
    return Array.from(filtered)
  }, [fieldTemplate?.dynamic_field_type?.subtypes, teamOptions])

  useEffect(() => {
    const pagesFromData = ftData.reduce((acc, cur) => {
      const page = acc?.find((it) => it.id == cur.page_template_id)
      if (!page) {
        return [
          ...acc,
          {
            id: cur.page_template_id,
            page_num: cur.page_num,
            page_template_name: cur.page_template_name,
          },
        ]
      }
      return acc
    }, [])
    setPages(pagesFromData)
  }, [ftData])

  useEffect(() => {
    if (fieldTemplate.is_subfield) {
      const tableFieldForSubfield = ftData.find(
        (field) =>
          field.data_structure == FIELD_TYPES.TABLE &&
          field.subfield_template_ids.split(',').includes(String(fieldTemplate.id)),
      )
      setTableField(tableFieldForSubfield)

      if (
        fieldTemplate.is_imported_field &&
        fieldTemplate.data_structure != FIELD_TYPES.TABLE &&
        tableFieldForSubfield
      ) {
        setFieldTemplate({
          ...fieldTemplate,
          pivot_field_template_id: tableFieldForSubfield?.pivot_field_template_id || null,
        })
      }
    }
  }, [ftData, initialFieldTemplate])

  const handleTabChange = (_, newValue) => setSelectedTabValue(newValue)

  useEffect(() => {
    const pivots = ftData.filter((it) => it.is_pivot_field == true)
    setPivotFields(pivots)

    if (fieldTemplate.pivot_field_template_id) {
      const pivotField = ftData.find((it) => it.id == fieldTemplate.pivot_field_template_id)
      if (tableField?.metadata?.linked_process_config?.import_from_linked_process) {
        setImportableFields(
          importedFieldList.filter(
            (it) =>
              it.process_template_id == tableField?.metadata?.linked_process_config?.linked_process,
          ),
        )
      } else {
        setImportableFields(
          importedFieldList.filter((it) =>
            pivotField?.import_from_process_template_id?.includes(it.process_template_id),
          ),
        )
      }
    }
  }, [fieldTemplate, importedFieldList, ftData, tableField])

  useEffect(() => {
    if (
      [FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(fieldTemplate?.data_structure) &&
      fieldTemplate.list_id
    ) {
      ;(async () => {
        const listItems = (await listDataService.getData(user, fieldTemplate?.list_id)).data
        setSelectedListItems(listItems)
      })()
    }
  }, [user, fieldTemplate])

  useEffect(() => {
    ;(async () => {
      const res = await Promise.all([
        fieldTemplateService.getDerivableFieldsForProcess(user, processTemplateId),
        adminProcessService.getProcesses(user),
        userConfig.getAllAllowedTeamsInProcess(user, processTemplateId, fieldTemplate.id),
      ])

      const updatedFields = res[0]?.map((it) => {
        const tf = it.is_subfield ? res[0].find((tf) => tf.id == it.table_field_template_id) : null
        return {
          ...it,
          field_template_name: tf
            ? tf.field_template_name + '/' + it.field_template_name
            : it.field_template_name,
          table_field_template: tf,
        }
      })

      setParentFieldTemplates(updatedFields.filter((it) => it.data_structure === FIELD_TYPES.TABLE))
      setDerivableFieldTemplate(updatedFields.filter((it) => it.id != fieldTemplate.id))
      setProcesses(res[1].filter((it) => it.id != processTemplateId))
      setAssignmentFieldData(res[2])
    })()
  }, [processTemplateId])

  useEffect(() => {
    setPivotFields(ftData.filter((it) => it.is_pivot_field == true))
  }, [ftData])

  useEffect(() => {
    if (fieldTemplate.pivot_field_template_id) {
      const pivotField = pivotFields.find((it) => it.id == fieldTemplate.pivot_field_template_id)
      setImportableFields(
        importedFieldList.filter((it) =>
          pivotField?.import_from_process_template_id?.includes(it.process_template_id),
        ),
      )
    }
  }, [fieldTemplate.pivot_field_template_id])

  useEffect(() => {
    setFieldTemplate((prev) => {
      const isNumericType = prev?.data_structure == FIELD_TYPES.NUMBER
      if (
        isNumericType &&
        (prev?.num_decimal_places === undefined || prev?.num_decimal_places === null)
      ) {
        return { ...prev, num_decimal_places: 2 }
      }
      if (
        !isNumericType &&
        prev?.num_decimal_places !== null &&
        prev?.num_decimal_places !== undefined
      ) {
        return { ...prev, num_decimal_places: null }
      }
      return prev
    })
  }, [fieldTemplate?.data_structure])

  const saveChanges = async (overrideTemplate) => {
    try {
      const isEvent =
        overrideTemplate &&
        typeof overrideTemplate === 'object' &&
        overrideTemplate.nativeEvent !== undefined

      const source = isEvent || !overrideTemplate ? fieldTemplate : overrideTemplate

      if (source.is_assignment_field == 'yes' && assignmentFieldData?.selection?.length <= 0) {
        throw new Error('Assigned team is mandatory for assignment field.')
      }

      const hasInvalidRegex = (() => {
        const pattern = source?.validation_rules?.regExPattern
        if (!pattern) return false
        try {
          new RegExp(pattern)
          return false
        } catch {
          return true
        }
      })()

      if (hasInvalidRegex) {
        throw new Error(
          'The regex pattern in Validation Rules is invalid. Please fix it before saving.',
        )
      }

      const finalField = onlyFieldColumns(transformFieldPropForAPI(source), allColumns)

      const result = await onChangeSave(finalField)

      if (result && source.is_assignment_field == 'yes') {
        updateAssignmentFieldTeamMap()
      }
    } catch (error) {
      console.error('Error saving field template changes:', error)
      toast.error(error.message)
    }
  }

  const saveComputationChanges = (field) => onChangeSave(field)

  const getAssociatedListValues = (ft, ls) =>
    ft?.list_id ? ls?.find((it) => ft.list_id == it.id) : undefined

  const updateAssignmentFieldTeamMap = async () => {
    try {
      await userConfig.updateAssignmentFieldTeamMap(user, fieldTemplate.id, {
        team_ids: assignmentFieldData.selection,
      })
      toast.success('Selected team(s) assigned to field!')
    } catch (error) {
      console.error('Error updating assignment field team map:', error)
      toast.error(error.message)
    }
  }

  const getAssignedTeams = (afd) => {
    if (afd?.allowed?.length <= 0) return []
    return afd?.selection?.map((it) => {
      const team = afd?.allowed?.find((t) => t.id == it)
      return { ...team, value: it, label: team?.name }
    })
  }

  return (
    <DialogWrapper
      fullWidth
      open={true}
      onClose={() => setDialogOpen(false)}
      heading={`${fieldTemplate?.field_template_name} (Field Settings)`}
      actions={<></>}
    >
      <TabContext value={selectedTabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleTabChange}
            aria-label="Field Setting Tabs"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="General" value="1" />
            <Tab label="Advance" value="2" />
            <Tab label="Computation" value="3" />
            {activityTemplate.activity_type == ACTIVITY_TYPES.LONG_RUNNING &&
              fieldTemplate.data_structure == FIELD_TYPES.TABLE && (
                <Tab label="Recurring Table" value="4" />
              )}
          </TabList>
        </Box>

        <TabPanel value="1" sx={{ padding: 0, paddingTop: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
            {/* Field template name */}
            <TextField
              type="text"
              value={fieldTemplate?.field_template_name}
              fullWidth
              size="small"
              variant="outlined"
              color="primary"
              label="Field Template Name"
              onChange={(e) =>
                setFieldTemplate({ ...fieldTemplate, field_template_name: e.target.value })
              }
            />

            {/* Field Type */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Choose Field Type</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Choose Field Type"
                value={fieldTemplate?.data_structure}
                onChange={(e) => {
                  const nextDataStructure = e.target.value
                  const prunedRules = pruneValidationRules(
                    fieldTemplate?.validation_rules,
                    nextDataStructure,
                  )

                  let updatedField = {
                    ...fieldTemplate,
                    list_id: null,
                    default_value: null,
                    data_structure: nextDataStructure,
                    validation_rules: prunedRules,
                  }

                  if (nextDataStructure !== FIELD_TYPES.LIST) {
                    setAssignmentFieldData({ ...assignmentFieldData, selection: [] })
                    updatedField = { ...updatedField, is_assignment_field: false }
                  }

                  setFieldTemplate(updatedField)
                }}
              >
                {Object.entries(FIELD_TYPES).map(([k, v], i) => {
                  if (fieldTemplate?.is_subfield == true && v == FIELD_TYPES.TABLE) return null
                  return (
                    <MenuItem key={i} value={v}>
                      {v}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>

            {/* Select List */}
            {[FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(fieldTemplate?.data_structure) && (
              <Autocomplete
                size="small"
                options={lists || []}
                value={getAssociatedListValues(fieldTemplate, lists)}
                getOptionLabel={(option) => option.list_name}
                onChange={(_, v) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    list_id: v?.id || null,
                    dynamic_field_type: null,
                  })
                }
                renderInput={(params) => <TextField {...params} label="Associated List" />}
              />
            )}

            {/* Placeholder Mapping UI */}
            {(() => {
              if (
                ![FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(
                  fieldTemplate?.data_structure,
                ) ||
                !fieldTemplate.list_id
                //  ||
                // fieldTemplate.is_derived_field === 'yes'
              ) {
                return null
              }

              const selectedList = lists.find((l) => l.id == fieldTemplate.list_id)
              const listPlaceholders = selectedList?.placeholders || []

              if (listPlaceholders.length === 0) return null

              return (
                <Box
                  sx={{
                    mt: 1,
                    mb: 1,
                    p: 2,
                    border: '1px dashed #ccc',
                    borderRadius: 1,
                    backgroundColor: '#f9f9f9',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#555',
                      mb: 1.5,
                      textTransform: 'uppercase',
                    }}
                  >
                    Placeholder Mappings
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {listPlaceholders.map((key) => {
                      const currentMap = fieldTemplate.metadata?.placeholder_map || {}
                      const mappedFieldId = currentMap[key]
                      const selectedField = ftData.find((f) => f.id == mappedFieldId)

                      return (
                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ minWidth: 100, fontSize: 13, fontWeight: 500, color: '#333' }}>
                            [[{key}]]
                          </Box>
                          <Autocomplete
                            size="small"
                            fullWidth
                            options={ftData.filter((f) => {
                              // 1. Never map to self
                              if (f.id == fieldTemplate.id) return false

                              // 2. Never map to a 'Table' field type itself
                              if (f.data_structure === FIELD_TYPES.TABLE) return false

                              const isCurrentSubfield =
                                fieldTemplate.is_subfield === 'yes' ||
                                fieldTemplate.is_subfield === true
                              const isTargetSubfield =
                                f.is_subfield === 'yes' || f.is_subfield === true

                              // If the current field is a subfield, target must either be a normal field, or a subfield in the same table
                              if (isCurrentSubfield) {
                                if (isTargetSubfield) {
                                  const currentTable =
                                    tableField ||
                                    ftData.find(
                                      (field) =>
                                        field.data_structure === FIELD_TYPES.TABLE &&
                                        field.subfield_template_ids &&
                                        field.subfield_template_ids
                                          .split(',')
                                          .map((s) => s.trim())
                                          .includes(String(fieldTemplate.id)),
                                    )

                                  if (!currentTable) return false

                                  const inSameTable = currentTable.subfield_template_ids
                                    .split(',')
                                    .map((s) => s.trim())
                                    .includes(String(f.id))

                                  if (!inSameTable) return false
                                }
                              } else {
                                // If the current field is a normal field, don't show subfields for selection
                                if (isTargetSubfield) return false
                              }

                              return true
                            })}
                            getOptionLabel={(option) => {
                              const isSub =
                                option.is_subfield === 'yes' || option.is_subfield === true
                              if (isSub) {
                                const parentTable = ftData.find(
                                  (field) =>
                                    field.data_structure === FIELD_TYPES.TABLE &&
                                    field.subfield_template_ids &&
                                    field.subfield_template_ids
                                      .split(',')
                                      .map((s) => s.trim())
                                      .includes(String(option.id)),
                                )
                                return parentTable
                                  ? `${parentTable.field_template_name}/${option.field_template_name}`
                                  : `${option.field_template_name} (Subfield)`
                              }
                              return option.field_template_name
                            }}
                            value={selectedField || null}
                            onChange={(_, v) => {
                              const newMetadata = { ...(fieldTemplate.metadata || {}) }
                              const currentMap = { ...(newMetadata.placeholder_map || {}) }
                              if (v && v.id) {
                                currentMap[key] = v.id
                              } else {
                                delete currentMap[key]
                              }
                              newMetadata.placeholder_map = currentMap
                              setFieldTemplate({ ...fieldTemplate, metadata: newMetadata })
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label={`Map to field...`} />
                            )}
                          />
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              )
            })()}

            {/* field template default value */}
            {fieldTemplate.data_structure != FIELD_TYPES.TABLE &&
              (fieldTemplate.is_subfield == 'yes' ? (
                <></>
              ) : (
                <DefaultValueField
                  fieldTemplate={fieldTemplate}
                  selectedListItems={selectedListItems}
                  setFieldTemplate={setFieldTemplate}
                />
              ))}

            {/* Digits after decimal */}
            {fieldTemplate.data_structure == FIELD_TYPES.NUMBER && (
              <TextField
                type="number"
                value={fieldTemplate?.num_decimal_places ?? 2}
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                label="Digits After Decimal Place"
                onChange={(e) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    num_decimal_places:
                      e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                  })
                }
              />
            )}

            {/* Required? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Required?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Required?"
                value={fieldTemplate?.is_required}
                onChange={(e) =>
                  setFieldTemplate({ ...fieldTemplate, is_required: e.target.value })
                }
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {/* Visible in my tasks card? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Visible in my tasks card?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Visible in my tasks card?"
                value={fieldTemplate?.is_visible_in_my_tasks_card}
                onChange={(e) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    is_visible_in_my_tasks_card: e.target.value,
                  })
                }
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {/* Is Read Only? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Is Read Only?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Is Read Only?"
                value={fieldTemplate?.is_read_only}
                disabled={fieldTemplate?.is_computed == 'yes'}
                onChange={(e) =>
                  setFieldTemplate({ ...fieldTemplate, is_read_only: e.target.value })
                }
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {/* Assignment Field? */}
            {fieldTemplate?.data_structure === FIELD_TYPES.LIST &&
              fieldTemplate?.is_subfield === 'no' && (
                <FormControl variant="outlined" fullWidth size="small" color="primary">
                  <InputLabel>Assignment Field?</InputLabel>
                  <Select
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    label="Assignment Field?"
                    value={fieldTemplate?.is_assignment_field}
                    onChange={(e) => {
                      setFieldTemplate({ ...fieldTemplate, is_assignment_field: e.target.value })
                      if (e.target.value == 'no')
                        setAssignmentFieldData({ ...assignmentFieldData, selection: [] })
                    }}
                  >
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>
              )}

            {fieldTemplate?.data_structure === FIELD_TYPES.LIST &&
              fieldTemplate?.is_subfield === 'no' &&
              fieldTemplate.is_assignment_field == 'yes' && (
                <Autocomplete
                  size="small"
                  multiple
                  options={
                    assignmentFieldData?.allowed?.map((it) => ({
                      ...it,
                      value: it.id,
                      label: it.name,
                    })) || []
                  }
                  value={getAssignedTeams(assignmentFieldData) || []}
                  getOptionLabel={(option) => option.label ?? ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Choose team(s)"
                      color="primary"
                    />
                  )}
                  onChange={(_, v) =>
                    setAssignmentFieldData({
                      ...assignmentFieldData,
                      selection: v?.map((it) => it.value),
                    })
                  }
                />
              )}

            {/* Selectable Table? */}
            {fieldTemplate?.data_structure === FIELD_TYPES.TABLE && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <InputLabel>Selectable Table?</InputLabel>
                <Select
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  label="Selectable Table?"
                  value={fieldTemplate?.is_selectable_table}
                  onChange={(e) =>
                    setFieldTemplate({ ...fieldTemplate, is_selectable_table: e.target.value })
                  }
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Allow Add Rows? */}
            {fieldTemplate?.data_structure === FIELD_TYPES.TABLE && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <InputLabel>Allow Add Rows?</InputLabel>
                <Select
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  label="Allow Add Rows?"
                  value={fieldTemplate?.allow_add_rows}
                  onChange={(e) =>
                    setFieldTemplate({ ...fieldTemplate, allow_add_rows: e.target.value })
                  }
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Allow Delete Rows? */}
            {fieldTemplate?.data_structure === FIELD_TYPES.TABLE && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <InputLabel>Allow Delete Rows?</InputLabel>
                <Select
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  label="Allow Delete Rows?"
                  value={fieldTemplate?.allow_delete_rows}
                  onChange={(e) =>
                    setFieldTemplate({ ...fieldTemplate, allow_delete_rows: e.target.value })
                  }
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Allow Excel Upload? */}
            {fieldTemplate?.data_structure === FIELD_TYPES.TABLE && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <InputLabel>Allow Excel Upload?</InputLabel>
                <Select
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  label="Allow Excel Upload?"
                  value={fieldTemplate?.allow_excel_upload || 'no'}
                  onChange={(e) =>
                    setFieldTemplate({ ...fieldTemplate, allow_excel_upload: e.target.value })
                  }
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* is_aggregation_table */}
            {fieldTemplate?.data_structure === FIELD_TYPES.TABLE && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <InputLabel>is_aggregation_table</InputLabel>
                <Select
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  label="is_aggregation_table?"
                  value={fieldTemplate?.is_aggregation_table}
                  onChange={(e) => {
                    if (e.target.value == 'no') {
                      setFieldTemplate({
                        ...fieldTemplate,
                        aggregation_parent_table_field_template_id: null,
                        is_aggregation_table: e.target.value,
                      })
                    } else {
                      setFieldTemplate({ ...fieldTemplate, is_aggregation_table: e.target.value })
                    }
                  }}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            )}

            {fieldTemplate?.is_aggregation_table == 'yes' && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  options={parentFieldTemplates}
                  size="small"
                  getOptionLabel={(item) => item?.field_template_name}
                  value={
                    parentFieldTemplates.find(
                      (item) =>
                        item.id == fieldTemplate?.aggregation_parent_table_field_template_id,
                    ) || null
                  }
                  onChange={(_, newValue) =>
                    setFieldTemplate({
                      ...fieldTemplate,
                      aggregation_parent_table_field_template_id: newValue?.id || null,
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select aggregation_parent_table_field_template_id"
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            )}

            {/* Derived Field? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Derived Field?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Derived Field?"
                value={fieldTemplate?.is_derived_field}
                onChange={(e) => {
                  if (e.target.value == 'no') {
                    setFieldTemplate({
                      ...fieldTemplate,
                      derived_from_field_template_id: null,
                      is_derived_field: e.target.value,
                    })
                  } else {
                    setFieldTemplate({
                      ...fieldTemplate,
                      is_derived_field: e.target.value,
                      is_read_only: e.target.value,
                    })
                  }
                }}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {fieldTemplate?.is_derived_field == 'yes' && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  multiple
                  id="select-derived-field"
                  size="small"
                  options={derivableFieldTemplates
                    .filter(
                      (it) =>
                        it.data_structure == fieldTemplate.data_structure &&
                        it.list_id == fieldTemplate.list_id,
                    )
                    .sort(
                      (a, b) => -b.activity_template_name.localeCompare(a.activity_template_name),
                    )}
                  groupBy={(option) => option?.activity_template_name}
                  getOptionLabel={(item) => item?.field_template_name}
                  value={
                    fieldTemplate?.derived_from_field_template_id
                      ?.split(',')
                      ?.map((id) => derivableFieldTemplates.find((it) => it.id == id)) || []
                  }
                  onChange={(_, newValue) =>
                    setFieldTemplate({
                      ...fieldTemplate,
                      derived_from_field_template_id: newValue.map((item) => item?.id).join(','),
                    })
                  }
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <GroupHeader>{params.group}</GroupHeader>
                      <GroupItems>{params.children}</GroupItems>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select derived field"
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            )}

            {/* Pivot Field? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Pivot Field?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Pivot Field?"
                value={fieldTemplate?.is_pivot_field}
                onChange={(e) => {
                  if (e.target.value == 'no') {
                    setFieldTemplate({
                      ...fieldTemplate,
                      import_from_process_template_id: null,
                      is_pivot_field: e.target.value,
                    })
                  } else {
                    setFieldTemplate({ ...fieldTemplate, is_pivot_field: e.target.value })
                  }
                }}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {fieldTemplate?.is_pivot_field == 'yes' && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  multiple
                  id="select-pivot-processes"
                  size="small"
                  options={processes}
                  getOptionLabel={(item) => item?.process_template_name}
                  value={
                    fieldTemplate?.import_from_process_template_id
                      ?.split(',')
                      ?.map((id) => processes.find((it) => it.id == id)) || []
                  }
                  onChange={(_, newValue) => {
                    setFieldTemplate({
                      ...fieldTemplate,
                      import_from_process_template_id:
                        newValue.length > 0 ? newValue.map((item) => item.id).join(',') : null,
                    })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select Processes"
                      color="primary"
                    />
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <GroupHeader>{params.group}</GroupHeader>
                      <GroupItems>{params.children}</GroupItems>
                    </li>
                  )}
                />
              </FormControl>
            )}

            {/* Imported Field? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Imported Field?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Imported Field?"
                value={fieldTemplate?.is_imported_field}
                onChange={(e) => {
                  if (e.target.value == 'no') {
                    setFieldTemplate({
                      ...fieldTemplate,
                      is_imported_field: e.target.value,
                      pivot_field_template_id: null,
                      imported_from_field_template_id: null,
                    })
                  } else {
                    setFieldTemplate({
                      ...fieldTemplate,
                      is_imported_field: e.target.value,
                      is_read_only: e.target.value,
                    })
                  }
                }}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {fieldTemplate?.is_imported_field == 'yes' && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  id="select-pivot-field"
                  size="small"
                  options={pivotFields}
                  getOptionLabel={(item) => item.field_template_name}
                  value={
                    fieldTemplate.pivot_field_template_id
                      ? ftData.find((it) => it.id == fieldTemplate.pivot_field_template_id)
                      : null
                  }
                  onChange={(_, newValue) =>
                    setFieldTemplate({
                      ...fieldTemplate,
                      pivot_field_template_id: newValue?.id || null,
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select Pivot Field"
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            )}

            <ImportFieldSelectionUI
              fieldTemplate={fieldTemplate}
              setFieldTemplate={setFieldTemplate}
              importableFields={importableFields}
              processTemplateId={processTemplateId}
            />

            {/* Hidden Field? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Hidden Field?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Hidden Field?"
                value={fieldTemplate?.is_hidden}
                onChange={(e) => setFieldTemplate({ ...fieldTemplate, is_hidden: e.target.value })}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {/* Hidden in Process Dashboard? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Hidden in Process Dashboard?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Hidden in Process Dashboard?"
                value={fieldTemplate?.is_hidden_in_process_dashboard}
                onChange={(e) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    is_hidden_in_process_dashboard: e.target.value,
                  })
                }
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {/* One Time Field? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>One Time Field?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="One Time Field?"
                value={fieldTemplate?.is_one_time_input}
                onChange={(e) =>
                  setFieldTemplate({ ...fieldTemplate, is_one_time_input: e.target.value })
                }
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {/* Is process counter? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Is process counter?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Process Counter?"
                value={fieldTemplate?.is_process_counter}
                onChange={(e) =>
                  setFieldTemplate({ ...fieldTemplate, is_process_counter: e.target.value })
                }
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {fieldTemplate?.is_process_counter == 'yes' && (
              <TextField
                type="number"
                value={fieldTemplate?.process_counter_initial_value ?? ''}
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                label="Counter Initial Value (default: 10001)"
                onChange={(e) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    process_counter_initial_value: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
            )}

            {/* Show in Long Running Process? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel>Show in Long Running Process?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                label="Show in Long Running Process?"
                value={fieldTemplate?.is_lrp_display}
                onChange={(e) =>
                  setFieldTemplate({ ...fieldTemplate, is_lrp_display: e.target.value })
                }
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            {/* Dynamic field type */}
            {[FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(fieldTemplate.data_structure) && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  id="dynamic_field_type"
                  size="small"
                  options={['users', 'teams']}
                  getOptionLabel={(option) => option}
                  value={fieldTemplate?.dynamic_field_type?.type}
                  onChange={(_, value) =>
                    setFieldTemplate({
                      ...fieldTemplate,
                      dynamic_field_type: { type: value },
                      list_id: null,
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Dynamic Field Type"
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            )}

            {/* Dynamic field type: teams subtypes */}
            {fieldTemplate?.dynamic_field_type?.type == 'teams' && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  multiple
                  id="dynamic_field_type"
                  options={teamOptions.types}
                  size="small"
                  getOptionLabel={(option) => option}
                  value={fieldTemplate?.dynamic_field_type?.subtypes || []}
                  style={{ marginBottom: '.8rem' }}
                  onChange={(_, value) =>
                    setFieldTemplate({
                      ...fieldTemplate,
                      dynamic_field_type: { ...fieldTemplate.dynamic_field_type, subtypes: value },
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Dynamic Field SubType"
                      color="primary"
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  id="dynamic_field_supertype"
                  options={filteredSubtypes}
                  size="small"
                  getOptionLabel={(option) => option}
                  value={fieldTemplate?.dynamic_field_type?.supertype || []}
                  onChange={(_, value) =>
                    setFieldTemplate({
                      ...fieldTemplate,
                      dynamic_field_type: {
                        ...fieldTemplate.dynamic_field_type,
                        supertype: Array.isArray(value) && value.length > 0 ? value : null,
                      },
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Dynamic Field SuperType"
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            )}

            {/* Dynamic field type: users filter */}
            {fieldTemplate?.dynamic_field_type?.type === 'users' && (
              <UserFilterSettings
                fieldTemplate={fieldTemplate}
                setFieldTemplate={setFieldTemplate}
              />
            )}

            {/* Page Name */}
            {fieldTemplate.is_subfield == 'no' && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  id="field_page"
                  size="small"
                  options={pages}
                  getOptionLabel={(option) => option.page_template_name}
                  value={pages?.find((it) => it.id == fieldTemplate?.page_template_id) || null}
                  onChange={(_, value) =>
                    setFieldTemplate({
                      ...fieldTemplate,
                      page_template_id: value?.id,
                      page_num: value?.page_num,
                      page_template_name: value?.page_template_name,
                    })
                  }
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Page Name" color="primary" />
                  )}
                />
              </FormControl>
            )}

            {/* Column width */}
            {fieldTemplate.is_subfield == 'yes' ? (
              <TextField
                type="number"
                value={fieldTemplate?.subfield_column_width_in_rem}
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                label="Column Width (REM)"
                onChange={(e) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    subfield_column_width_in_rem: e.target.value,
                  })
                }
              />
            ) : (
              <TextField
                type="number"
                value={fieldTemplate?.field_width_in_rem_for_process_dashboard}
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                label="Process Dashboard Width (REM)"
                onChange={(e) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    field_width_in_rem_for_process_dashboard: e.target.value,
                  })
                }
              />
            )}

            <Box sx={{ display: 'flex', columnGap: 2, width: '100%', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" variant="contained" onClick={() => saveChanges()}>
                Save
              </Button>
            </Box>
          </Box>
        </TabPanel>

        {/* ── ADVANCE TAB ────────────────────────────────────────────────── */}
        <TabPanel value="2" sx={{ padding: 0, paddingTop: 2 }}>
          <ConditionalEnableDisableUI
            field={fieldTemplate}
            activityTemplateId={activityTemplateId}
          />

          {fieldTemplate.is_subfield == 'yes' ? (
            <>
              <Divider sx={{ my: 2 }} />
              <TableFieldConstraintUI
                field={fieldTemplate}
                ftData={ftData}
                activityTemplateId={activityTemplateId}
                processTemplateId={processTemplateId}
              />
              <Divider sx={{ my: 2 }} />
              <FieldConstraintUI field={fieldTemplate} activityTemplateId={activityTemplateId} />
            </>
          ) : (
            <>
              <Divider sx={{ my: 2 }} />
              <FieldConstraintUI field={fieldTemplate} activityTemplateId={activityTemplateId} />
            </>
          )}

          {fieldTemplate.is_subfield == 'yes' && (
            <>
              <Divider sx={{ my: 2 }} />
              <AggregationTables field={fieldTemplate} activityTemplateId={activityTemplateId} />
            </>
          )}

          {[FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(fieldTemplate?.data_structure) && (
            <>
              <Divider sx={{ my: 2 }} />
              <ParentFieldSetupUI field={fieldTemplate} activityTemplateId={activityTemplateId} />
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <FieldVisibilityProtectionUI field={fieldTemplate} />

          {/* ── Validation Rules ── */}
          <Divider sx={{ my: 2 }} />
          <ValidationRulesUI
            fieldTemplate={fieldTemplate}
            setFieldTemplate={setFieldTemplate}
            onSave={saveChanges}
          />

          {fieldTemplate?.is_pivot_field == 'yes' && (
            <>
              <Divider sx={{ my: 2 }} />
              <PivotFieldFilterSetup
                fieldTemplate={fieldTemplate}
                activityTemplateId={activityTemplateId}
                importedFieldList={importedFieldList}
              />
            </>
          )}

          {fieldTemplate?.is_pivot_field == 'yes' && (
            <>
              <Divider sx={{ my: 2 }} />
              <PreventProcessCreationUI
                fieldTemplate={fieldTemplate}
                processTemplateId={processTemplateId}
                activityTemplateId={activityTemplateId}
              />
            </>
          )}
        </TabPanel>

        {/* ── COMPUTATION TAB ─────────────────────────────────────────────── */}
        <TabPanel value="3" sx={{ padding: 0, paddingTop: 2 }}>
          <FieldComputationSetupUI
            field={onlyFieldColumns(transformFieldPropForAPI(fieldTemplate), allColumns)}
            setFieldTemplate={setFieldTemplate}
            activityTemplateId={activityTemplateId}
            processTemplateId={processTemplateId}
            saveChanges={saveComputationChanges}
          />
        </TabPanel>

        {/* ── RECURRING TABLE TAB ─────────────────────────────────────────── */}
        <TabPanel value="4" sx={{ padding: 0, paddingTop: 2 }}>
          <RecurringTableSetup
            fieldTemplate={fieldTemplate}
            derivableFieldTemplates={derivableFieldTemplates}
          />
        </TabPanel>
      </TabContext>
    </DialogWrapper>
  )
}

export default EditFieldTemplateDialog
